import React, { useEffect, useState } from 'react';
import Animated, {
  runOnJS, useAnimatedReaction, useAnimatedStyle, useSharedValue, withTiming,
} from 'react-native-reanimated';
import Svg, { Ellipse, Polygon } from 'react-native-svg';
import { StyleSheet } from 'react-native';
import Colors from '../../constants/Colors';

export const occupancyColor = (
  value: number,
  secondary: boolean = false,
  focused: boolean = false,
) => {
  if (value >= 0 && typeof value === 'number') {
    if (value === 0) {
      return secondary ? `${Colors.light.hereworksGreen600}${focused ? '68' : '30'}` : Colors.light.hereworksGreen500;
    }
    if (value <= 50) {
      return secondary ? `${Colors.light.hereworksBlue600}${focused ? '68' : '30'}` : Colors.light.hereworksBlue500;
    }
    if (value <= 70) {
      return secondary ? `${Colors.light.hereworksYellow700}${focused ? '68' : '30'}` : Colors.light.hereworksYellow500;
    }
    if (value) {
      return secondary ? `${Colors.light.hereworksRed700}${focused ? '68' : '30'}` : Colors.light.hereworksRed500;
    }
  }
  return secondary ? `${Colors.light.hereworksBlack300}${focused ? '68' : '30'}`
    : Colors.light.hereworksPrimaryGrey700;
};

export default function AreaView(props) {
  const translateXarea = useSharedValue<number>(0);
  const translateYarea = useSharedValue<number>(0);
  const { occupancy }: { occupancy: number } = props;

  const [isFocused, setIsFocused] = useState<boolean>(false);

  useAnimatedReaction(
    () => props.focusedLocationId?.value === props.area.LocationId,
    (focusedValue, previousFocusedValue) => {
      if (focusedValue !== previousFocusedValue) {
        runOnJS(setIsFocused)(focusedValue);
      }
    },
  );

  const areaColour = (focused: boolean) => {
    if (props.smartGuard) {
      return props.motionDetected ? `${Colors.light.hereworksRed500}${focused ? '68' : '30'}` : 'transparent';
    }
    if (props.occupancyShown) return occupancyColor(occupancy, true, focused);
    return Colors.light.hereworksBlue500;
  };

  const areaStrokeDash = () => {
    if (props.ticketsSelected) return null;
    return showOccupancyColor ? null : [8, 8];
  };

  const areaFillOpacity = isFocused ? 68 : 30;
  const fill: string = props.occupancyShown ? areaColour(isFocused)
    : `${Colors.light.hereworksBlue500}${areaFillOpacity}`;
  const negativeScale = useSharedValue<number>(props.negativeScale ?? 1);
  const strokeWidth: number = isFocused || props.areaSelected ? 2 : 0;

  useEffect(() => {
    negativeScale.value = withTiming(props.negativeScale);
  }, [props]);

  const transformStyleArea = useAnimatedStyle(() => ({
    // @ts-ignore
    transform: [
      {
        translateX: translateXarea.value,
      },
      {
        translateY: translateYarea.value,
      },
    ],
  }));

  const showOccupancyColor: boolean = props.occupancyShown && !props.areaSelected;

  const { polygon } = props.area;

  // const shape = `${polygon},ellipse`;

  const isEllipse = polygon.includes('ellipse');

  const DrawEllipse = () => {
    if (!props.area.points || props.area.points.length < 4) {
      return null;
    }
    const p0 = props.area.points[0];
    const p1 = props.area.points[1];
    const p2 = props.area.points[2];
    const p3 = props.area.points[3];

    const cx = (p0.x + p2.x) / 2;
    const cy = (p0.y + p2.y) / 2;

    const vectorA = {
      x: p1.x - p0.x,
      y: p1.y - p0.y,
    };
    const vectorB = {
      x: p3.x - p0.x,
      y: p3.y - p0.y,
    };

    const rx = Math.sqrt(vectorB.x ** 2 + vectorB.y ** 2) / 2;
    const ry = Math.sqrt(vectorA.x ** 2 + vectorA.y ** 2) / 2;

    return (
      <Ellipse
        cx={cx}
        cy={cy}
        rx={rx}
        ry={ry}
        fill={fill}
        stroke={areaColour(isFocused)}
        strokeWidth={((props.areaSelected && !props.isSpace && !props.spaceSelected)
          || (props.isSpace && props.spaceSelected)
          || props.ticketsSelected) ? 2 : strokeWidth}
        strokeDasharray={areaStrokeDash()}
      />);
  };

  return (
    <React.Fragment key={props.index}>
      <Animated.View style={[transformStyleArea, styles.container]}>
        <Svg style={styles.svg}>
          {isEllipse ? (<DrawEllipse />) : <Polygon
            key={props.index}
            points={polygon}
            fill={fill}
            stroke={areaColour(isFocused)}
            strokeWidth={((props.areaSelected && !props.isSpace && !props.spaceSelected)
              || (props.isSpace && props.spaceSelected) || props.ticketsSelected) ? 2 : strokeWidth}
            strokeDasharray={areaStrokeDash()}
          />}
        </Svg>
      </Animated.View>
    </React.Fragment>
  );
}

const styles = StyleSheet.create({
  container: {
    width: 2200,
    height: '100%',
    position: 'absolute',
  },
  svg: {
    zIndex: 15,
    position: 'absolute',
    width: '100%',
    height: '100%',
  },
});
